import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { X, Plus, Loader2, Trash2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/stores/useToastStore'
import {
  useRetailPricing,
  useSaveRetailPricing,
  useDeleteRetailPricing,
} from '@/hooks/useRetailPricing'
import { useDropdownLookups } from '@/hooks/useDagTable'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import type { PlanDetailItem } from '@/types'

interface RetailPricingDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: PlanDetailItem | null
  planId: string
  versionId: string
}

interface PricingRow {
  key: number
  country_code: string
  currency_code: string
  retail_price: string
  imu_pct: number | null
  notes: string
}

function createBlankRow(key: number): PricingRow {
  return {
    key,
    country_code: '',
    currency_code: '',
    retail_price: '',
    imu_pct: null,
    notes: '',
  }
}

export function RetailPricingDrawer({
  open,
  onOpenChange,
  item,
  planId,
  versionId,
}: RetailPricingDrawerProps) {
  const [rows, setRows] = useState<PricingRow[]>([createBlankRow(0)])
  const [isExistingData, setIsExistingData] = useState(false)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const keyCounterRef = useRef(1)
  const hasHydratedRef = useRef(false)

  // Fetch dropdown data for pricing_country
  const { data: dropdownData } = useDropdownLookups()
  const countryOptions = useMemo(
    () => dropdownData?.categories['pricing_country'] ?? [],
    [dropdownData]
  )

  // Fetch retail pricing when drawer opens
  const {
    data: pricingData,
    isLoading,
    isFetched,
  } = useRetailPricing(
    open ? planId : undefined,
    open ? versionId : undefined,
    open ? item?.id : undefined
  )

  const saveMutation = useSaveRetailPricing(planId, versionId)
  const deleteMutation = useDeleteRetailPricing(planId, versionId)

  const isSaving = saveMutation.isPending

  // Populate local state from fetched data (only once per drawer open)
  useEffect(() => {
    if (!isFetched || !open || hasHydratedRef.current) return
    hasHydratedRef.current = true

    if (pricingData && pricingData.items && pricingData.items.length > 0) {
      const hydratedRows: PricingRow[] = pricingData.items.map(apiItem => {
        const key = keyCounterRef.current++
        return {
          key,
          country_code: apiItem.country_code,
          currency_code: apiItem.currency_code,
          retail_price: String(apiItem.retail_price),
          imu_pct: apiItem.imu_pct,
          notes: apiItem.notes ?? '',
        }
      })
      setRows(hydratedRows)
      setIsExistingData(true)
    } else {
      setRows([createBlankRow(0)])
      keyCounterRef.current = 1
      setIsExistingData(false)
    }
  }, [pricingData, isFetched, open])

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setRows([createBlankRow(0)])
      keyCounterRef.current = 1
      setIsExistingData(false)
      setConfirmRemoveOpen(false)
      hasHydratedRef.current = false
    }
  }, [open])

  const handleAddRow = useCallback(() => {
    const key = keyCounterRef.current++
    setRows(prev => [...prev, createBlankRow(key)])
  }, [])

  const handleRemoveRow = useCallback((key: number) => {
    setRows(prev => {
      if (prev.length <= 1) return prev
      return prev.filter(r => r.key !== key)
    })
  }, [])

  const handleUpdateRow = useCallback(
    (key: number, field: keyof PricingRow, value: string) => {
      setRows(prev =>
        prev.map(row => {
          if (row.key !== key) return row
          return { ...row, [field]: value }
        })
      )
    },
    []
  )

  // Countries already used (to filter out of dropdown for other rows)
  const usedCountryCodes = useMemo(
    () => new Set(rows.map(r => r.country_code).filter(Boolean)),
    [rows]
  )

  const handleConfirmRemove = useCallback(() => {
    if (!item) return
    deleteMutation.mutate(
      { lineId: item.id },
      {
        onSuccess: () => {
          setConfirmRemoveOpen(false)
          onOpenChange(false)
        },
      }
    )
  }, [item, deleteMutation, onOpenChange])

  const handleSave = () => {
    if (!item) return

    // Validate: all rows must have country_code and retail_price > 0
    const hasInvalidRows = rows.some(
      row =>
        !row.country_code || !row.retail_price || Number(row.retail_price) <= 0
    )

    if (hasInvalidRows) {
      toast.error(
        'Each row must have a country and a retail price greater than 0'
      )
      return
    }

    const items = rows.map(row => ({
      country_code: row.country_code,
      currency_code: row.currency_code,
      retail_price: Number(row.retail_price),
      landed_cost: item.landedCost ?? null,
      notes: row.notes || null,
    }))

    saveMutation.mutate(
      { lineId: item.id, body: { items } },
      { onSuccess: () => onOpenChange(false) }
    )
  }

  if (!item) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-200 flex-col sm:max-w-200"
      >
        {/* Header */}
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <SheetTitle className="text-lg font-semibold">
                {isExistingData ? 'Edit Pricing Model' : 'Create Pricing Model'}
              </SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground">
                SKU #{item.skuNumber}
              </SheetDescription>
            </div>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="border border-[#6d8bbe] rounded-md overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-[1fr_1fr_0.7fr_0.8fr_0.8fr_0.6fr_1.4fr_40px] bg-[#f8f8f8] border-b border-[#d6dae1]">
                  <div className="px-2 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Country
                  </div>
                  <div className="px-2 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Currency Code
                  </div>
                  <div className="px-2 py-2 text-xs font-semibold text-[#1c1c1c]">
                    US Price
                  </div>
                  <div className="px-2 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Retail Price
                  </div>
                  <div className="px-2 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Landed Cost
                  </div>
                  <div className="px-2 py-2 text-xs font-semibold text-[#1c1c1c]">
                    IMU %
                  </div>
                  <div className="px-2 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Notes
                  </div>
                  <div />
                </div>

                {/* Table Rows */}
                {rows.map(row => {
                  const availableCountries = countryOptions.filter(
                    opt =>
                      opt.value === row.country_code ||
                      !usedCountryCodes.has(opt.value)
                  )

                  return (
                    <div
                      key={row.key}
                      className="grid grid-cols-[1fr_1fr_0.7fr_0.8fr_0.8fr_0.6fr_1.4fr_40px] border-b border-[#d6dae1] last:border-b-0"
                    >
                      {/* Country */}
                      <div className="px-2 py-2">
                        <Select
                          value={row.country_code}
                          onValueChange={val =>
                            handleUpdateRow(row.key, 'country_code', val)
                          }
                        >
                          <SelectTrigger className="h-8 text-xs border-[#d6dae1] rounded-md cursor-pointer">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableCountries.map(opt => (
                              <SelectItem
                                key={opt.value}
                                value={opt.value}
                                className="cursor-pointer"
                              >
                                {opt.display_label ?? opt.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Currency Code */}
                      <div className="px-2 py-2">
                        <Input
                          value={row.currency_code}
                          onChange={e =>
                            handleUpdateRow(
                              row.key,
                              'currency_code',
                              e.target.value
                            )
                          }
                          className="h-8 text-xs placeholder:text-xs px-2 border-[#d6dae1] rounded-md"
                          placeholder="e.g. USD"
                        />
                      </div>

                      {/* US Price */}
                      <div className="px-3 py-2 flex items-center">
                        <span className="text-xs text-muted-foreground">
                          {pricingData?.us_final_retail != null
                            ? `$${Number(pricingData.us_final_retail).toFixed(2)}`
                            : '—'}
                        </span>
                      </div>

                      {/* Retail Price */}
                      <div className="px-2 py-2">
                        <Input
                          type="number"
                          value={row.retail_price}
                          onChange={e =>
                            handleUpdateRow(
                              row.key,
                              'retail_price',
                              e.target.value
                            )
                          }
                          className="h-8 text-xs placeholder:text-xs px-2 border-[#d6dae1] rounded-md"
                          placeholder="0.00"
                          min={0}
                          step="0.01"
                        />
                      </div>

                      {/* Landed Cost (read-only, from DAG table) */}
                      <div className="px-3 py-2 flex items-center">
                        <span className="text-xs text-muted-foreground">
                          {item?.landedCost != null
                            ? `$${Number(item.landedCost).toFixed(2)}`
                            : '—'}
                        </span>
                      </div>

                      {/* IMU % (read-only) */}
                      <div className="px-3 py-2 flex items-center">
                        <span className="text-xs text-muted-foreground">
                          {row.imu_pct != null
                            ? `${Number(row.imu_pct).toFixed(1)}%`
                            : '—'}
                        </span>
                      </div>

                      {/* Notes */}
                      <div className="px-2 py-2">
                        <Input
                          value={row.notes}
                          onChange={e =>
                            handleUpdateRow(row.key, 'notes', e.target.value)
                          }
                          className="h-8 text-xs placeholder:text-xs px-2 border-[#d6dae1] rounded-md"
                          placeholder="Optional notes"
                        />
                      </div>

                      {/* Delete */}
                      <div className="flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveRow(row.key)}
                          disabled={rows.length <= 1}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Add Row */}
              <button
                onClick={handleAddRow}
                className="mt-3 flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 cursor-pointer"
              >
                <Plus className="h-4 w-4" />
                Add Row
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="border-t px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <div>
              {isExistingData && (
                <button
                  type="button"
                  className="text-sm text-destructive hover:text-destructive/80 underline cursor-pointer"
                  onClick={() => setConfirmRemoveOpen(true)}
                >
                  Remove Pricing Model
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className=""
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isExistingData ? 'Save Pricing Model' : 'Create Pricing Model'}
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>

      <ConfirmationDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title="Remove Pricing Model"
        description="Are you sure you want to remove the pricing model? This action cannot be undone."
        variant="destructive"
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
        onConfirm={handleConfirmRemove}
      />
    </Sheet>
  )
}
